import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const LoansClosedByAdmin = () => {
  const { user } = useAuth();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchLoans();
  }, []);

  const fetchLoans = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/officer/loans/closed-by-admin', {
        params: { officerId: user.userId },
      });
      setLoans(response.data || []);
    } catch (err) {
      console.error('Error fetching loans closed by admin:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load loans');
      setLoans([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return 'Rs 0.00';
    return `Rs ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDateTime = (dateTime) => {
    if (!dateTime) return 'N/A';
    return new Date(dateTime).toLocaleString();
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Loans Closed by Admin
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        This section shows all loans that were closed by administrators. When a loan is closed by admin, 
        any due payments and arrears are automatically deducted from system capital.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : loans.length === 0 ? (
        <Alert severity="info">No loans closed by admin found.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Loan ID</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Customer</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>NIC</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Loan Amount</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Arrears Deducted</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Closed By</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Closed At</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loans.map((loan) => (
                <TableRow key={loan.id} hover>
                  <TableCell>{loan.id}</TableCell>
                  <TableCell>{loan.customer?.fullName || loan.customer?.name || 'N/A'}</TableCell>
                  <TableCell>{loan.customer?.nic || 'N/A'}</TableCell>
                  <TableCell>{formatCurrency(loan.approvedAmount || loan.requestedAmount)}</TableCell>
                  <TableCell>
                    {loan.arrearsAmount && loan.arrearsAmount > 0 ? (
                      <Chip 
                        label={formatCurrency(loan.arrearsAmount)} 
                        color="error" 
                        size="small"
                      />
                    ) : (
                      <Chip label="No arrears" color="success" size="small" />
                    )}
                  </TableCell>
                  <TableCell>
                    {loan.closedByAdminUser?.fullName || 'Admin'}
                  </TableCell>
                  <TableCell>{formatDateTime(loan.closedAt)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default LoansClosedByAdmin;
